/**
 * Search-result facts for /tournament/<id> pages, shared by the edge (functions/_middleware.ts, the head the crawler reads), the
 * pages (src/pages/Tournament.tsx, Run.tsx, src/components/tournament/shared.tsx, whose Meta rewrites the head after hydration) and
 * the tests: the description kept to what a result shows, the breadcrumb, the schema.org type of a run's dates.
 */

const ORIGIN = 'https://tixinfo.com';

/** A meta description search engines show whole: at most `max` characters, cut at a sentence end when one fits, else at a word. */
export const clampDescription = (/** @type {string} */ text, max = 160) => {
  const s = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (s.length <= max) return s;
  const sentences = s.split(/(?<=\.)\s+/);
  let out = '';
  for (const part of sentences) {
    const next = out ? `${out} ${part}` : part;
    if (next.length > max) break;
    out = next;
  }
  if (out.length >= 70) return out;
  const cut = s.slice(0, max - 1);
  const at = cut.lastIndexOf(' ');
  return `${(at > 40 ? cut.slice(0, at) : cut).replace(/[\s,;:.\-–—]+$/, '')}…`;
};

const CATEGORY_PATH = { Music: '/music', Sports: '/sports', Comedy: '/comedy', Theater: '/theater' };

/** BreadcrumbList for a tournament, tour or show page: Home > category > the page (the same shape as an event page's, functions/_middleware.ts). */
export const tournamentBreadcrumb = (/** @type {string} */ category, /** @type {string} */ name) => {
  const home = { '@type': 'ListItem', position: 1, name: 'Home', item: `${ORIGIN}/` };
  const path = CATEGORY_PATH[/** @type {keyof typeof CATEGORY_PATH} */ (category)];
  const items = path ? [home, { '@type': 'ListItem', position: 2, name: category, item: ORIGIN + path }] : [home];
  items.push({ '@type': 'ListItem', position: items.length + 1, name });
  return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
};

/** The schema.org type of one date of a run, by the category the build wrote. */
export const runEventType = (/** @type {string} */ category) =>
  category === 'Theater' ? 'TheaterEvent' : category === 'Comedy' ? 'ComedyEvent' : category === 'Sports' ? 'SportsEvent' : 'MusicEvent';

/** Venues the hubs name that we know the town and country of (Google's Event data wants location.address). */
const KNOWN_VENUES = {
  'alexandra palace': ['London', 'GB'],
  'principality stadium': ['Cardiff', 'GB'],
  'inalpi arena': ['Turin', 'IT'],
  'iga stadium': ['Montreal', 'CA'],
  'indian wells tennis garden': ['Indian Wells', 'US'],
  'usta billie jean king national tennis center': ['New York', 'US'],
  'sofi stadium': ['Inglewood', 'US'],
};
/** A place that is not one place ("Clubs across Spain", "Various venues"): no address to give. */
const MANY_PLACES = /\b(?:across|various|clubs|arenas in|stadiums in|circuits in|speedways|campus)\b/i;

/**
 * A schema.org Place for an event's venue. `name` is shown as given; the PostalAddress (town, and the
 * ISO country when known) comes from the town the page knows, a "Venue, Town" name, or a venue we know,
 * and is left off where the place is not a single known one. Never invents a town.
 * @param {string | null | undefined} name
 * @param {{ city?: string | null, cc?: string | null }} [where]
 */
export const placeLd = (name, where = {}) => {
  const label = String(name ?? '').replace(/\s*\([^)]*\)/g, '').trim();
  /** @type {Record<string, unknown>} */
  const place = { '@type': 'Place', name: label };
  if (!label || MANY_PLACES.test(label)) return place;
  const parts = label.split(/,\s*/);
  const venue = parts[0].toLowerCase();
  const known = /** @type {Record<string, string[]>} */ (KNOWN_VENUES)[venue];
  const city = (where.city || '').trim() || (parts.length === 2 ? parts[1] : '') || (known ? known[0] : '');
  if (!city || MANY_PLACES.test(city)) return place;
  const cc = /^[A-Za-z]{2}$/.test(where.cc ?? '') ? String(where.cc).toUpperCase() : known ? known[1] : '';
  place.address = { '@type': 'PostalAddress', addressLocality: city, ...(cc ? { addressCountry: cc } : {}) };
  return place;
};
